import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, MessageCircle, Phone, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { errorMessage } from "../../lib/stays";
import { useContent } from "../../context/SiteContentContext";
import { useAdminData } from "../AdminContext";

export default function Messages() {
  const [rows, setRows] = useState(null);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [open, setOpen] = useState(null);
  const { reloadBadges } = useAdminData();
  const { get } = useContent();

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/contact-messages", { params: onlyUnread ? { unread: 1 } : {} })).data); }
    catch (e) { toast.error(errorMessage(e, "Couldn't load messages")); setRows([]); }
  }, [onlyUnread]);
  useEffect(() => { load(); }, [load]);

  const setRead = async (m, is_read) => {
    try { await api.patch(`/admin/contact-messages/${m.id}`, { is_read }); await load(); reloadBadges(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/admin/contact-messages/${m.id}`); await load(); reloadBadges(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const toggleOpen = (m) => { setOpen(open === m.id ? null : m.id); if (!m.is_read) setRead(m, true); };

  return (
    <div className="space-y-4" data-testid="admin-messages">
      <label className="flex items-center gap-2 text-sm text-white/60"><input type="checkbox" className="accent-[#C9A24B]" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} /> Show unread only</label>
      {rows === null ? <div className="glass h-24 animate-pulse" /> : rows.length === 0 ? <div className="text-center text-white/40 py-12">No messages</div> : rows.map((m) => (
        <article key={m.id} className={`glass p-5 ${m.is_read ? "" : "border-l-2 border-gold"}`} data-testid={`message-${m.id}`}>
          <button type="button" onClick={() => toggleOpen(m)} aria-expanded={open === m.id} className="w-full text-left flex justify-between items-start gap-4">
            <div className="min-w-0">
              <div className={`font-display text-lg ${m.is_read ? "text-white/70" : "text-white"}`}>{m.subject}</div>
              <div className="text-xs text-white/40 mt-1">{m.name} · {m.email}{m.phone && ` · ${m.phone}`}</div>
              {open !== m.id && <p className="text-sm text-white/45 mt-2 truncate">{m.message}</p>}
            </div>
            <div className="text-xs text-white/30 shrink-0">{new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</div>
          </button>
          {open === m.id && (
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              <div className="flex flex-wrap gap-3 mt-5 text-xs">
                <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + m.subject)}`} className="btn-gold !py-2 !px-4 flex items-center gap-2"><Mail size={13} /> Reply by email</a>
                {m.phone && <a href={`https://wa.me/${m.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-4 flex items-center gap-2"><MessageCircle size={13} /> WhatsApp</a>}
                {m.phone && <a href={`tel:${m.phone}`} className="btn-ghost !py-2 !px-4 flex items-center gap-2"><Phone size={13} /> Call</a>}
                <button onClick={() => setRead(m, !m.is_read)} className="btn-ghost !py-2 !px-4 flex items-center gap-2"><MailOpen size={13} /> Mark as {m.is_read ? "unread" : "read"}</button>
                <button onClick={() => remove(m)} className="text-red-400 flex items-center gap-2 px-2" aria-label="Delete message"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          )}
        </article>
      ))}
      <p className="text-xs text-white/30">Replies go from your own email app. Your site contact email is {get("site.email")}.</p>
    </div>
  );
}
